import * as Sentry from '@sentry/react';
import { Form, withFormik } from 'formik';
import { connect } from 'react-redux';

import type { UnifiedEvent } from 'src/app/analytics/analyticsEvents';
import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import Button from 'src/app/components/Elements/Button';
import LinkWithQuery from 'src/app/components/Elements/LinkWithQuery';
import TextInput from 'src/app/components/Form/TextInput';
import { extractValidationErrors } from 'src/app/popsicle/utils';
import { hipaa, loggedOutPromoOnboardingWaitlistSuccess, privacy, termsAndConditions } from 'src/app/router/paths';
import { pushWithQueryParams, segmentTrack, setCurrentOnboardingStep } from 'src/app/state/actions';
import { createLeadAction } from 'src/app/state/actions/leadForm';
import { getCurrentPathname, getPromoSignUpZipCode } from 'src/app/state/selectors';
import type { Dispatch, RootState } from 'src/app/state/store';

import { STEP_NAME } from '../StepLayout/consts';
import { ButtonContainer, LegalMessage, StyledButton } from '../styles';

import { validation } from './validation';

const mapStateToProps = (state: RootState) => ({
  gettingStartedZipCode: state.onboardingForm.zipCode,
  promoSignUpZipCode: getPromoSignUpZipCode(state),
  pathname: getCurrentPathname(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  createLead: async body => {
    await dispatch(createLeadAction(body));
  },
  dispatchSegmentTrack: (event: UnifiedEvent, payload) => dispatch(segmentTrack(event, payload)),
  onSetCurrentStepToWaitlistConfirmation: () => dispatch(setCurrentOnboardingStep(STEP_NAME.WAITLIST_CONFIRMATION)),
  onSetCurrentStepToWelcome: () => dispatch(setCurrentOnboardingStep(STEP_NAME.WELCOME)),
  redirectToWaitListSuccess: () => dispatch(pushWithQueryParams(loggedOutPromoOnboardingWaitlistSuccess.toPath())),
});

const enhancedWithFormik = withFormik({
  mapPropsToValues: validation.initialValues,
  validationSchema: validation.schema,
  validateOnChange: true,
  validateOnBlur: false,
  validateOnMount: true,
  handleSubmit: async (values, { props, setErrors, setSubmitting }) => {
    const {
      onSetCurrentStepToWaitlistConfirmation,
      gettingStartedZipCode,
      promoSignUpZipCode,
      createLead,
      dispatchSegmentTrack,
      pathname,
      isGettingStartedForm,
      redirectToWaitListSuccess,
    } = props;

    setSubmitting(true);

    try {
      const body = {
        zipCode: isGettingStartedForm ? gettingStartedZipCode : promoSignUpZipCode,
        email: values.email,
        source: 'onboarding_stay_in_touch',
      };
      await createLead(body);
      dispatchSegmentTrack(UNIFIED.joinDeliveryAreaWaitlist, { pagepath: pathname });
      setSubmitting(false);
      if (isGettingStartedForm) {
        onSetCurrentStepToWaitlistConfirmation();
      } else {
        redirectToWaitListSuccess();
      }
      dispatchSegmentTrack(UNIFIED.createLead);
      dispatchSegmentTrack(UNIFIED.createWaitlistLead);
    } catch (error) {
      Sentry.captureException(error);
      setErrors(extractValidationErrors(error));
      setSubmitting(false);
    }
  },
});
const Renderer = ({ handleSubmit, isValid, onSetCurrentStepToWelcome, isGettingStartedForm }) => {
  return (
    <Form>
      <TextInput name="email" type="text" label="Email" />
      <ButtonContainer>
        <StyledButton size="medium" onClick={handleSubmit} disabled={!isValid} type="submit" style={'capsuleBlue50'}>
          Notify Me
        </StyledButton>
        {isGettingStartedForm && (
          <Button style="tertiary" className="pl-0" size="small" onClick={onSetCurrentStepToWelcome} type="button">
            Try Another Zip Code
          </Button>
        )}
      </ButtonContainer>
      <LegalMessage>
        By clicking notify me, I accept the{' '}
        <LinkWithQuery to={termsAndConditions.toPath()} target="_blank">
          Capsule Terms of Service
        </LinkWithQuery>
        {', '}
        <LinkWithQuery to={privacy.toPath()} target="_blank">
          Privacy Policy
        </LinkWithQuery>
        {', and '}
        <LinkWithQuery to={hipaa.toPath()} target="_blank">
          HIPAA Policy
        </LinkWithQuery>
      </LegalMessage>
    </Form>
  );
};

const EnhancedJoinWaitlistForm = enhancedWithFormik(Renderer);
export const JoinWaitListForm = connect(mapStateToProps, mapDispatchToProps)(EnhancedJoinWaitlistForm);
